import { z } from 'zod';
/**
 * Analyst todos — a personal task list scoped to an org. Each todo is
 * owned by exactly one user (`ownerUid`) and visible only to them
 * within that org. Optionally linked to an entity, an investigation,
 * or both — the link is what makes a todo more than a notepad: from a
 * todo card the analyst can jump into the dossier or the investigation
 * the work is about.
 *
 * Storage path: `todos/{todoId}`. Firestore rules enforce owner-only
 * read AND owner-only create/update/delete (matching the `notes` and
 * `threads` collections). Production traffic flows through the
 * `/api/todos*` endpoints so the server can stamp timestamps + emit
 * audit events; the client-write rules exist as a fallback path and
 * to keep the access model uniform with the rest of the app.
 */
export const TodoStatus = z.enum(['todo', 'in_progress', 'done']);
export const TodoPriority = z.enum(['low', 'normal', 'high']);
export const Todo = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    ownerUid: z.string().min(1),
    title: z.string().min(1).max(280),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus,
    priority: TodoPriority,
    /** Optional link to an entity in the shared graph. */
    entityName: z.string().optional(),
    /** Optional link to an investigation. */
    investigationId: z.string().optional(),
    /** ISO date (YYYY-MM-DD) — date-only, no time part. */
    dueDate: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    completedAt: z.string().optional(),
});
export const CreateTodoRequest = z.object({
    title: z.string().min(1).max(280),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus.default('todo'),
    priority: TodoPriority.default('normal'),
    entityName: z.string().optional(),
    investigationId: z.string().optional(),
    dueDate: z.string().optional(),
});
export const UpdateTodoRequest = z.object({
    title: z.string().min(1).max(280).optional(),
    notes: z.string().max(4_000).optional(),
    status: TodoStatus.optional(),
    priority: TodoPriority.optional(),
    entityName: z.string().nullable().optional(),
    investigationId: z.string().nullable().optional(),
    dueDate: z.string().nullable().optional(),
});
export const ListTodosResponse = z.object({
    todos: z.array(Todo),
});
